import { Button, CloseButton, Drawer, Flex } from '@chakra-ui/react';
import { FormInput, FormSwitch, Loading, toaster, useDrawer } from '@shared';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateInboundNumber, useAddInboundNumber, useGetSingleInboundNumber } from '../../api/hooks/useInboundNumberSuperadmin';
import { useGetAllAccounts } from '../../api/hooks/useAccountSuperadmin';

type InboundNumberForm = {
	phone: string;
	isEnabled: boolean;

	reservationAccountID: number;
	reservationTimeStart: Date | string | null;
	reservationTimeEnd: Date | string | null;
};

export default function SuperadminInboundNumberDrawer({ inboundNumberID }: { inboundNumberID?: number }) {
	const { t } = useTranslation();
	const { mutate: updateInboundNumber, isPending: isUpdatingInboundNumber } = useUpdateInboundNumber();
	const { mutate: addInboundNumber, isPending: isAddingInboundNumber } = useAddInboundNumber();

	const { closeDrawer } = useDrawer();
	const queryClient = useQueryClient();
	const { data: accounts, isFetching: isPendingAccounts } = useGetAllAccounts();

	const { data: inboundNumber, isFetching: isFetchingInboundNumber } = useGetSingleInboundNumber(inboundNumberID as number, {
		isEnabled: !!inboundNumberID,
	});

	const methods = useForm<InboundNumberForm>({
		defaultValues: {
			phone: '',
			isEnabled: inboundNumber?.isEnabled || false,
		},
	});

	const {
		handleSubmit,
		reset,
		formState: { dirtyFields, isDirty },
	} = methods;

	useEffect(() => {
		if (inboundNumber) {
			reset({
				...inboundNumber,
				reservationTimeStart: inboundNumber.inboundNumberReservations?.find(reservation => reservation.isActive)?.timeStart || '',
				reservationTimeEnd: inboundNumber.inboundNumberReservations?.find(reservation => reservation.isActive)?.timeEnd || '',
			});
		}
	}, [inboundNumber, reset]);

	function saveInboundNumber(values: InboundNumberForm): void {
		if (inboundNumberID) {
			patchInboundNumber(values);
		} else {
			const payload: AddInboundNumberRequest = {
				phone: values.phone,
				isEnabled: values.isEnabled,
			};

			addInboundNumber(payload, {
				onSuccess: async () => {
					toaster.create({
						title: t('general.saved'),
						type: 'success',
					});
					queryClient.refetchQueries({ queryKey: ['inboundNumbers'] });
					queryClient.refetchQueries({ queryKey: ['inboundNumber'] });

					closeDrawer('inboundNumberDrawer');
				},
			});
		}
	}

	function patchInboundNumber(values: InboundNumberForm): void {
		if (!inboundNumberID) {
			return;
		}

		const payload: UpdateInboundNumberRequest = {
			inboundNumberID: inboundNumberID,
		};

		(Object.keys(dirtyFields) as Array<keyof InboundNumberForm>).forEach(key => {
			const value = values[key];

			if (key === 'isEnabled') {
				payload[key] = value as boolean;
			}

			if (key === 'phone') {
				payload[key] = value as string;
			}
		});

		updateInboundNumber(payload, {
			onSuccess: async () => {
				toaster.create({
					title: t('general.saved'),
					type: 'success',
				});
				queryClient.refetchQueries({ queryKey: ['inboundNumbers'] });
				queryClient.refetchQueries({ queryKey: ['inboundNumber'] });

				closeDrawer('inboundNumberDrawer');
			},
		});
	}

	if (isPendingAccounts) {
		return <Loading />;
	}

	if (!accounts) {
		throw new Error('Unable to fetch accounts.');
	}

	return isFetchingInboundNumber ? (
		<Loading />
	) : (
		<FormProvider {...methods}>
			<form style={{ height: '100%', display: 'flex', flexDirection: 'column' }} onSubmit={handleSubmit(saveInboundNumber)}>
				<Drawer.Header>
					<Drawer.Title marginRight="20px" wordBreak={'break-word'}>
						{inboundNumberID ? t('general.edit_inbound_number') : t('general.add_inbound_number')}
					</Drawer.Title>
				</Drawer.Header>
				<Drawer.Body flex={1}>
					<Flex flexDirection={'column'} gap={4} marginBottom={3} width={'100%'}>
						<FormInput type="tel" isRequired label={t('general.phone')} name="phone" maxLength={20} />
						<FormSwitch label={t('general.is_enabled')} name="isEnabled" />
					</Flex>
				</Drawer.Body>
				<Drawer.Footer>
					<Button onClick={() => closeDrawer('inboundNumberDrawer')} variant="outline">
						{t('general.cancel')}
					</Button>
					<Button type="submit" disabled={isUpdatingInboundNumber || isAddingInboundNumber || !isDirty}>
						{isUpdatingInboundNumber ? t('general.please_wait') : t('general.save')}
					</Button>
				</Drawer.Footer>

				<Drawer.CloseTrigger asChild>
					<CloseButton size="sm" />
				</Drawer.CloseTrigger>
			</form>
		</FormProvider>
	);
}
